import { useMutation } from '@tanstack/react-query';
import { useRef, useState, type ChangeEvent, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';

import {
  changeMyPassword,
  deleteMyAvatar,
  updateMyProfile,
  uploadMyAvatar,
  type ChangePasswordPayload,
  type MyProfileUpdatePayload,
} from '../api/profile';
import { AppShell } from '../components/AppShell';
import { UserAvatar } from '../components/UserAvatar';
import { Button, Card, Input, SectionHeader, useActionDialog } from '../components/ui';
import { useAuth } from '../features/auth/AuthProvider';
import { AdminTelegramSettings } from '../features/notifications/components/AdminTelegramSettings';
import styles from './ProfilePage.module.scss';

const ALLOWED_AVATAR_TYPES = ['image/png', 'image/jpeg', 'image/webp'];
const MAX_AVATAR_BYTES = 5 * 1024 * 1024;
const MIN_PASSWORD_LENGTH = 8;

export function ProfilePage() {
  const { t } = useTranslation();
  const { user } = useAuth();

  return (
    <AppShell>
      <div className={styles.page}>
        <SectionHeader title={t('profile.title')} />
        {user ? (
          <div className={styles.sections}>
            <AvatarSection />
            <PersonalInfoSection />
            <PasswordSection />
            <AdminTelegramSettings />
          </div>
        ) : null}
      </div>
    </AppShell>
  );
}

function AvatarSection() {
  const { t } = useTranslation();
  const { confirmAction } = useActionDialog();
  const { user, tokens, refreshCurrentUser } = useAuth();
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [error, setError] = useState<string | null>(null);

  const uploadMutation = useMutation({
    mutationFn: (file: File) => uploadMyAvatar(file, tokens!.access_token),
    onSuccess: async () => {
      await refreshCurrentUser();
    },
  });

  const deleteMutation = useMutation({
    mutationFn: () => deleteMyAvatar(tokens!.access_token),
    onSuccess: async () => {
      await refreshCurrentUser();
    },
  });

  function pickFile() {
    fileInputRef.current?.click();
  }

  function handleFileChange(event: ChangeEvent<HTMLInputElement>) {
    setError(null);
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) {
      return;
    }
    if (!ALLOWED_AVATAR_TYPES.includes(file.type)) {
      setError(t('profile.avatarInvalidType'));
      return;
    }
    if (file.size > MAX_AVATAR_BYTES) {
      setError(t('profile.avatarTooLarge'));
      return;
    }
    uploadMutation.mutate(file);
  }

  async function handleRemove() {
    setError(null);
    if (!(await confirmAction({
      message: t('profile.removeConfirm'),
      confirmLabel: t('dialog.delete'),
      tone: 'danger',
    }))) {
      return;
    }
    deleteMutation.mutate();
  }

  const isBusy = uploadMutation.isPending || deleteMutation.isPending;
  const mutationError =
    uploadMutation.error instanceof Error
      ? uploadMutation.error.message
      : deleteMutation.error instanceof Error
        ? deleteMutation.error.message
        : null;

  if (!user) {
    return null;
  }

  return (
    <Card padding="lg">
      <SectionHeader title={t('profile.avatarSection')} compact />
      <div className={styles.avatarRow}>
        <UserAvatar user={user} size={120} />
        <div className={styles.avatarActions}>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/png,image/jpeg,image/webp"
            hidden
            onChange={handleFileChange}
          />
          <Button variant="primary" onClick={pickFile} disabled={isBusy}>
            {uploadMutation.isPending ? t('profile.uploading') : t('profile.changePhoto')}
          </Button>
          {user.avatar_url ? (
            <Button variant="ghost" onClick={handleRemove} disabled={isBusy}>
              {deleteMutation.isPending ? t('profile.removing') : t('profile.removePhoto')}
            </Button>
          ) : null}
        </div>
      </div>
      {error ? <p className={styles.errorText}>{error}</p> : null}
      {mutationError ? (
        <p className={styles.errorText}>{t('profile.avatarUploadError')}: {mutationError}</p>
      ) : null}
    </Card>
  );
}

function PersonalInfoSection() {
  const { t } = useTranslation();
  const { user, tokens, refreshCurrentUser } = useAuth();
  const [fullName, setFullName] = useState(user?.full_name ?? '');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [phoneNumber, setPhoneNumber] = useState(user?.phone_number ?? '');
  const [telegramUsername, setTelegramUsername] = useState(user?.telegram_username ?? '');
  const [success, setSuccess] = useState(false);

  const mutation = useMutation({
    mutationFn: (payload: MyProfileUpdatePayload) => updateMyProfile(payload, tokens!.access_token),
    onSuccess: async () => {
      await refreshCurrentUser();
      setSuccess(true);
    },
  });

  if (!user) {
    return null;
  }

  const trimmedFull = fullName.trim();
  const trimmedFirst = firstName.trim();
  const trimmedLast = lastName.trim();
  const trimmedPhone = phoneNumber.trim();
  const trimmedTelegram = telegramUsername.trim();

  const payload: MyProfileUpdatePayload = {
    ...(trimmedFull && trimmedFull !== (user.full_name ?? '') ? { full_name: trimmedFull } : {}),
    ...(trimmedFirst ? { first_name: trimmedFirst } : {}),
    ...(trimmedLast ? { last_name: trimmedLast } : {}),
    ...(trimmedPhone && trimmedPhone !== (user.phone_number ?? '') ? { phone_number: trimmedPhone } : {}),
    ...(trimmedTelegram && trimmedTelegram !== (user.telegram_username ?? '') ? { telegram_username: trimmedTelegram } : {}),
  };
  const hasChanges = Object.keys(payload).length > 0;

  function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setSuccess(false);
    if (!hasChanges) {
      return;
    }
    mutation.mutate(payload);
  }

  const errorMessage = mutation.error instanceof Error ? mutation.error.message : null;

  return (
    <Card padding="lg">
      <SectionHeader title={t('profile.personalInfoSection')} compact />
      <form className={styles.form} onSubmit={handleSubmit}>
        <Input
          label={t('profile.fullName')}
          value={fullName}
          onChange={(event) => {
            setFullName(event.target.value);
            setSuccess(false);
          }}
          autoComplete="name"
        />
        <div className={styles.row2}>
          <Input
            label={t('profile.firstName')}
            value={firstName}
            placeholder={t('profile.optionalPlaceholder')}
            onChange={(event) => {
              setFirstName(event.target.value);
              setSuccess(false);
            }}
            autoComplete="given-name"
          />
          <Input
            label={t('profile.lastName')}
            value={lastName}
            placeholder={t('profile.optionalPlaceholder')}
            onChange={(event) => {
              setLastName(event.target.value);
              setSuccess(false);
            }}
            autoComplete="family-name"
          />
        </div>
        <Input
          label={t('profile.phoneNumber')}
          value={phoneNumber}
          onChange={(event) => {
            setPhoneNumber(event.target.value);
            setSuccess(false);
          }}
          autoComplete="tel"
          inputMode="tel"
        />
        <Input
          label={t('profile.telegramUsername')}
          value={telegramUsername}
          placeholder={t('profile.telegramHelp')}
          onChange={(event) => {
            setTelegramUsername(event.target.value);
            setSuccess(false);
          }}
        />
        <div className={styles.formActions}>
          <Button type="submit" variant="primary" disabled={!hasChanges || mutation.isPending}>
            {mutation.isPending ? t('profile.saving') : t('profile.savePersonal')}
          </Button>
        </div>
        {success ? <p className={styles.successText}>{t('profile.saveSuccess')}</p> : null}
        {errorMessage ? <p className={styles.errorText}>{errorMessage}</p> : null}
      </form>
    </Card>
  );
}

function PasswordSection() {
  const { t } = useTranslation();
  const { tokens } = useAuth();
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmNewPassword, setConfirmNewPassword] = useState('');
  const [clientError, setClientError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const mutation = useMutation({
    mutationFn: (payload: ChangePasswordPayload) =>
      changeMyPassword(payload, tokens!.access_token),
    onSuccess: () => {
      setCurrentPassword('');
      setNewPassword('');
      setConfirmNewPassword('');
      setSuccess(true);
    },
  });

  function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setSuccess(false);
    setClientError(null);

    if (newPassword.length < MIN_PASSWORD_LENGTH) {
      setClientError(t('profile.passwordTooShort', { count: MIN_PASSWORD_LENGTH }));
      return;
    }
    if (newPassword !== confirmNewPassword) {
      setClientError(t('profile.passwordMismatch'));
      return;
    }
    mutation.mutate({ current_password: currentPassword, new_password: newPassword });
  }

  const errorMessage =
    clientError ?? (mutation.error instanceof Error ? mutation.error.message : null);

  const canSubmit =
    currentPassword.length > 0 && newPassword.length > 0 && confirmNewPassword.length > 0;

  return (
    <Card padding="lg">
      <SectionHeader title={t('profile.passwordSection')} compact />
      <form className={styles.form} onSubmit={handleSubmit}>
        <Input
          type="password"
          label={t('profile.currentPassword')}
          value={currentPassword}
          onChange={(event) => {
            setCurrentPassword(event.target.value);
            setSuccess(false);
            setClientError(null);
          }}
          autoComplete="current-password"
          required
        />
        <Input
          type="password"
          label={t('profile.newPassword')}
          value={newPassword}
          onChange={(event) => {
            setNewPassword(event.target.value);
            setSuccess(false);
            setClientError(null);
          }}
          autoComplete="new-password"
          required
        />
        <Input
          type="password"
          label={t('profile.confirmNewPassword')}
          value={confirmNewPassword}
          onChange={(event) => {
            setConfirmNewPassword(event.target.value);
            setSuccess(false);
            setClientError(null);
          }}
          autoComplete="new-password"
          required
        />
        <div className={styles.formActions}>
          <Button
            type="submit"
            variant="primary"
            disabled={!canSubmit || mutation.isPending}
          >
            {mutation.isPending ? t('profile.saving') : t('profile.changePassword')}
          </Button>
        </div>
        {success ? <p className={styles.successText}>{t('profile.passwordSuccess')}</p> : null}
        {errorMessage ? <p className={styles.errorText}>{errorMessage}</p> : null}
      </form>
    </Card>
  );
}
